import moment from 'moment'

// Everything the "unresolved" queue can filter, classify or label lives HERE, once -- the same
// arrangement Documents/utils.tsx uses for the ordinary page.
//
// PHASE 2 accepting the automatic user match of a row marked auto_resolve_available
// (POST .../unresolved/:id/resolve-user-auto) sends the row id plus, in the body, the
// expected_user_id the operator confirmed -- a stale-confirmation precondition only: the server
// re-decides the candidate and refuses with 409 if it is no longer that user.

// The enum tokens the API returns. Presentation Hebrew is mapped here and never sent back:
// the API's vocabulary is what the filters speak.
export const ISSUES = {
    MISSING_USER: 'MISSING_USER',
    INVALID_USER_REFERENCE: 'INVALID_USER_REFERENCE',
    MISSING_SKU: 'MISSING_SKU'
}

export const ISSUE_TEXT: { [key: string]: string } = {
    MISSING_USER: 'לקוח לא שויך',
    INVALID_USER_REFERENCE: 'שיוך לקוח שבור',
    MISSING_SKU: 'מק"ט חסר'
}

// MISSING and DANGLING are deliberately different words: "never attributed" and "attributed to
// a customer who is no longer there" are different jobs for the operator.
export const USER_STATE_TEXT: { [key: string]: string } = {
    VALID: 'תקין',
    MISSING: 'לא שויך',
    DANGLING: 'שיוך שבור'
}

export const SKU_STATE_TEXT: { [key: string]: string } = { VALID: 'תקין', MISSING: 'חסר' }

export const RESOLUTION_MODE_TEXT: { [key: string]: string } = {
    AUTO_AVAILABLE: 'ניתן לפתרון אוטומטי',
    MANUAL_USER_REQUIRED: 'דרוש שיוך ידני',
    MANUAL_SKU_REQUIRED: 'דרוש מק"ט ידני',
    MANUAL_USER_AND_SKU_REQUIRED: 'דרושים שיוך ומק"ט',
    INVESTIGATION_REQUIRED: 'דרושה בדיקה'
}

// UNIQUE is the only state the approved attribution rule would act on. INCONCLUSIVE is a single
// candidate whose provenance is not authoritative -- deliberately not called "unique", so the
// column never suggests a write the backend would refuse.
export const INSTRUMENT_STATE_TEXT: { [key: string]: string } = {
    UNIQUE: 'התאמה יחידה',
    AMBIGUOUS: 'מספר מועמדים',
    INCONCLUSIVE: 'לא חד-משמעי',
    NONE: 'אין התאמה',
    NOT_APPLICABLE: '—'
}

export const SKU_CANDIDATE_TEXT: { [key: string]: string } = {
    NONE: 'אין מקור מקומי',
    MALFORMED: 'טקסט ללא קוד',
    ATOMIC: 'קוד יחיד',
    AMBIGUOUS: 'יותר מקוד אחד',
    OVER_WIDTH: 'קוד ארוך מהעמודה',
    ADMINISTRATIVE: 'קוד עמלה בלבד',
    NOT_APPLICABLE: '—'
}

// The filter dropdowns.
//
// AUTO_AVAILABLE and MANUAL_USER_REQUIRED are intentionally absent. Both describe the SAME set
// of rows as far as SQL is concerned -- "user problem, sku fine" -- and which of the two a row
// finally reports is decided by instrument evidence the server only gathers AFTER pagination.
// The API therefore refuses both as filter values, and a dropdown must never offer an option
// that deterministically produces a 400. Both are still DISPLAYED in the table's status column.
export const ISSUE_OPTIONS = [
    { value: '', label: 'הכל' },
    { value: ISSUES.MISSING_USER, label: ISSUE_TEXT.MISSING_USER },
    { value: ISSUES.INVALID_USER_REFERENCE, label: ISSUE_TEXT.INVALID_USER_REFERENCE },
    { value: ISSUES.MISSING_SKU, label: ISSUE_TEXT.MISSING_SKU }
]

export const RESOLUTION_MODE_OPTIONS = [
    { value: '', label: 'הכל' },
    { value: 'MANUAL_SKU_REQUIRED', label: RESOLUTION_MODE_TEXT.MANUAL_SKU_REQUIRED },
    { value: 'MANUAL_USER_AND_SKU_REQUIRED', label: RESOLUTION_MODE_TEXT.MANUAL_USER_AND_SKU_REQUIRED }
]

// Mirrors FILTERABLE_RESOLUTION_MODES in api/app/Utils/tranzilaUnresolvedQuery.js. Exported so
// a test can assert the dropdown and the API accept exactly the same vocabulary.
export const FILTERABLE_RESOLUTION_MODES = ['MANUAL_SKU_REQUIRED', 'MANUAL_USER_AND_SKU_REQUIRED']

export const USER_STATE_OPTIONS = [
    { value: '', label: 'הכל' },
    { value: 'MISSING', label: USER_STATE_TEXT.MISSING },
    { value: 'DANGLING', label: USER_STATE_TEXT.DANGLING },
    { value: 'VALID', label: USER_STATE_TEXT.VALID }
]

export const DEFAULT_PER_PAGE = 20

// NO default date window, unlike the Documents page. That window exists there to bound a
// browse over ~1M rows; here the whole queue is a few hundred rows and hiding the old ones
// would hide most of the work. The predicate joins users and reads an unindexed column either
// way, so a date default would cost the same query and answer less of the question.
export const emptyUnresolvedFilters = () => ({
    date_from: null,
    date_to: null,
    terminal: '',
    transaction_index: '',
    issue: '',
    user_state: '',
    resolution_mode: ''
})

export const initialUnresolvedFilters = () => emptyUnresolvedFilters()

const textValue = value => (value === null || value === undefined ? '' : String(value).trim())

// A filter object whose every field is copied. The queue has no array filter today, so a flat
// copy is enough -- but it goes through one helper so a future list filter cannot be added
// without this being the place that learns about it.
export const cloneUnresolvedFilters = filters => Object.assign({}, filters || {})

// FLAT scalar params only, exactly like the Documents payload builder. Empty filters are
// omitted so the query string stays readable.
export const buildUnresolvedFilterPayload = filters => {
    const source = filters || {}
    const payload: any = {}

    if (source.date_from) payload.date_from = moment(source.date_from).format('YYYY-MM-DD')
    if (source.date_to) payload.date_to = moment(source.date_to).format('YYYY-MM-DD')

    for (const key of ['terminal', 'transaction_index', 'issue', 'user_state', 'resolution_mode']) {
        const value = textValue(source[key])
        if (value) payload[key] = value
    }
    return payload
}

export const buildUnresolvedPayload = (filters, page, perPage) =>
    Object.assign({ page: page, per_page: perPage }, buildUnresolvedFilterPayload(filters))

// One Hebrew sentence per structured backend refusal; a raw exception is never shown.
export const unresolvedError = payload => {
    const data = payload || {}
    const code = data.code || data.err_key
    if (code === 'TRANZILA_DOCUMENTS_UNRESOLVED_INVALID_FILTER') return 'אחד ממסנני הרשימה אינו תקין'
    return 'שגיאה בטעינת הרשומות הלא פתורות'
}

export const issueText = issue => ISSUE_TEXT[issue] || issue
export const userStateText = state => USER_STATE_TEXT[state] || state
export const skuStateText = state => SKU_STATE_TEXT[state] || state
export const resolutionModeText = mode => RESOLUTION_MODE_TEXT[mode] || mode
export const instrumentStateText = state => INSTRUMENT_STATE_TEXT[state] || state
export const skuCandidateText = state => SKU_CANDIDATE_TEXT[state] || state

// Colour carries the same distinction the words do, so a scan down the column separates
// "never attributed" from "attributed to nobody" without reading.
export const USER_STATE_COLOR: { [key: string]: string } = { VALID: 'green', MISSING: 'orange', DANGLING: 'red' }
export const ISSUE_COLOR: { [key: string]: string } = {
    MISSING_USER: 'orange',
    INVALID_USER_REFERENCE: 'red',
    MISSING_SKU: 'blue'
}

// One antd PRESET per treatment mode. Only presets: antd 3 renders any other `color` value --
// 'default' included -- as a custom colour, which forces white text onto what is then an
// invalid, ignored background, i.e. white on the default near-white. The palette follows the
// issue colours above: user work orange, sku work blue, both at once volcano.
export const RESOLUTION_MODE_COLOR: { [key: string]: string } = {
    AUTO_AVAILABLE: 'green',
    MANUAL_USER_REQUIRED: 'orange',
    MANUAL_SKU_REQUIRED: 'blue',
    MANUAL_USER_AND_SKU_REQUIRED: 'volcano',
    INVESTIGATION_REQUIRED: 'purple'
}

// The preset's own step-6 text measures only ~2.2-3.0:1 on its step-1 tint for green, orange,
// blue and volcano -- readable, but not "clearly". The tint and border stay (that is the Tag
// language used everywhere in this admin); only the TEXT moves to the same hue's step 8 from
// antd's palette, which is >= 5:1 on every tint (WCAG AA for normal text).
export const RESOLUTION_MODE_TEXT_COLOR: { [key: string]: string } = {
    AUTO_AVAILABLE: '#237804',
    MANUAL_USER_REQUIRED: '#ad4e00',
    MANUAL_SKU_REQUIRED: '#0050b3',
    MANUAL_USER_AND_SKU_REQUIRED: '#ad2102',
    INVESTIGATION_REQUIRED: '#391085'
}

// What the לקוח cell says when there is no current client to link to. Cell text, not the
// user_state filter vocabulary above: the filter names a state, the cell describes the row.
export const CLIENT_CELL_TEXT = {
    MISSING: 'לא משויך',
    DANGLING: 'לקוח לא קיים'
}

// The EXISTING client page, the same route Documents/List links to. Never a new one.
export const clientPagePath = userId => `/clients/${userId}`

// Offered when the SERVER says the row's user attribution is AUTO-safe (auto_resolve_available) and
// names the candidate: AUTO_AVAILABLE rows, and MANUAL_USER_AND_SKU_REQUIRED rows with the same
// evidence (their mode still names both problems). The server re-checks everything, including that
// its candidate is still the one confirmed here, so this only decides whether a button is drawn.
export const AUTO_USER_MODES = ['AUTO_AVAILABLE', 'MANUAL_USER_AND_SKU_REQUIRED']
export const canAcceptAutoMatch = row =>
    !!row &&
    AUTO_USER_MODES.indexOf(row.resolution_mode) !== -1 &&
    row.auto_resolve_available === true &&
    row.auto_resolve_user_id !== null &&
    row.auto_resolve_user_id !== undefined &&
    String(row.auto_resolve_user_id) !== ''

// Transaction number and proposed client id only: the queue never carries the candidate's name,
// and no payment instrument is ever shown.
export const autoMatchConfirmText = row =>
    `לשייך את עסקה ${row.transaction_index} ללקוח מס׳ ${row.auto_resolve_user_id}?`

export const RESOLUTION_CHANGED = 'TRANZILA_DOCUMENT_RESOLUTION_CHANGED'

// 'changed': the server refused because the row no longer qualifies -> say so and reload.
// 'error': anything else -> the row stays as it is.
export const autoMatchOutcome = error => {
    const response = error && error.response
    const data = (response && response.data) || {}
    if ((response && response.status === 409) || data.code === RESOLUTION_CHANGED) return 'changed'
    return 'error'
}

// After a fix the queue can shrink under the page on screen: accepting the only row of the last
// page leaves that page empty. Returns the page to load instead, from the SERVER's own total and
// last_page, or null when the requested page is still valid (no extra request).
export const fallbackPageAfterReload = (requestedPage, payload, perPage) => {
    const data = payload || {}
    const total = Number(data.total) || 0
    if (total === 0) return null
    const lastPage = Number(data.last_page) || Math.ceil(total / (Number(data.per_page) || perPage || 1))
    return lastPage >= 1 && requestedPage > lastPage ? lastPage : null
}

export const AUTO_MATCH_TEXT = {
    success: 'השיוך אושר',
    changed: 'העסקה השתנתה מאז הטעינה. הרשימה עודכנה.',
    error: 'אישור השיוך נכשל'
}

// Manual assignment: offered on MANUAL_USER_REQUIRED rows and -- user-first -- on
// MANUAL_USER_AND_SKU_REQUIRED rows, where only the user is assigned and the row stays in the
// queue as MANUAL_SKU_REQUIRED. The candidates are loaded on demand when the modal opens and the
// server re-decides mode and membership again on submit.
//
// Only when the server's own evidence says a candidate exists. instrument_match_state is the
// approved classifier's verdict over the SAME eligibility-filtered siblings the candidates endpoint
// reads, so it maps exactly: AMBIGUOUS = 2+ candidates, INCONCLUSIVE = 1, NONE = none (no empty
// modal). UNIQUE is deliberately absent: that is the only state the server offers "אשר שיוך" for,
// so a row never shows both user actions.
export const MANUAL_USER_MODES = ['MANUAL_USER_REQUIRED', 'MANUAL_USER_AND_SKU_REQUIRED']
export const MANUAL_CANDIDATE_STATES = ['AMBIGUOUS', 'INCONCLUSIVE']
export const canAssignManualUser = row =>
    !!row &&
    MANUAL_USER_MODES.indexOf(row.resolution_mode) !== -1 &&
    !!row.evidence &&
    MANUAL_CANDIDATE_STATES.indexOf(row.evidence.instrument_match_state) !== -1

export const CANDIDATE_VIA_TEXT = {
    card: 'כרטיס',
    bank: 'חשבון בנק',
    both: 'כרטיס וחשבון בנק'
}

// "<name>  #<id>" -- the name is production-only on the server, so the id always carries it.
export const candidateLabel = candidate =>
    candidate.user_name ? `${candidate.user_name}  #${candidate.user_id}` : `#${candidate.user_id}`

// Secondary line: how the instrument points at this user, and how many rows say so. Counts only.
export const candidateDetail = candidate => {
    const rows = Number(candidate.support_rows) || 0
    const via = CANDIDATE_VIA_TEXT[candidate.via] || ''
    return `${via} · ${rows === 1 ? 'התאמה אחת' : `${rows} התאמות`}`
}

// Auto SKU V1: offered on MANUAL_SKU_REQUIRED rows only, when the server says the row's own
// product string determines one code. The server recomputes that code on submit.
export const canAcceptAutoSku = row =>
    !!row &&
    row.resolution_mode === 'MANUAL_SKU_REQUIRED' &&
    row.auto_resolve_sku_available === true &&
    typeof row.auto_resolve_sku === 'string' &&
    row.auto_resolve_sku !== ''

// Transaction number and the code only: no product string, no item, no payment value.
export const autoSkuConfirmText = row => `לשייך לעסקה ${row.transaction_index} את המק״ט ${row.auto_resolve_sku}?`

export const AUTO_SKU_TEXT = {
    success: 'המק״ט אושר',
    changed: AUTO_MATCH_TEXT.changed,
    error: 'אישור המק״ט נכשל'
}

export const MANUAL_ASSIGN_TEXT = {
    success: 'הלקוח שויך',
    changed: AUTO_MATCH_TEXT.changed,
    error: 'שיוך הלקוח נכשל',
    loadError: 'טעינת המועמדים נכשלה',
    empty: 'לא נמצאו מועמדים לשיוך'
}

// Manual SKU V1: the operator picks a code for a MANUAL_SKU_REQUIRED row the server marks
// manual_sku_available (user exists and is not DELETED, sku missing, NO local code). The server
// decides that flag; this never infers it from the terminal or the product string. Never shown
// together with "אשר מק״ט": that action owns every row with a local code.
export const canAssignManualSku = row =>
    !!row &&
    row.resolution_mode === 'MANUAL_SKU_REQUIRED' &&
    row.manual_sku_available === true &&
    !canAcceptAutoSku(row)

// HISTORY IS ADVISORY. These sentences describe evidence; none of them says which code is right,
// and nothing in the modal selects or submits a code on its own.
export const skuHistoryHintText = evidence => {
    const data = evidence || {}
    if (!data.history_hint_sku) return null
    if (data.classification === 'MATCHING_NEIGHBORS') return `המק״ט לפני ואחרי העסקה זהה: ${data.history_hint_sku}`
    if (data.classification === 'ONE_SIDED_SINGLE_HISTORY')
        return `המק״ט היחיד שנמצא בהיסטוריה: ${data.history_hint_sku}`
    return null
}

export const skuCountText = count => (Number(count) === 1 ? 'עסקה אחת' : `${Number(count) || 0} עסקאות`)

// The full server list, with the customer's own history codes first. Never narrowed to history:
// NO_HISTORY and TRANSITION rows must stay resolvable with any known code.
export const manualSkuPickerGroups = evidence => {
    const data = evidence || {}
    const options = Array.isArray(data.sku_options) ? data.sku_options : []
    const known = (Array.isArray(data.history) ? data.history : []).map(entry => entry.sku)
    const fromHistory = known.map(sku => options.filter(option => option.sku === sku)[0]).filter(Boolean)
    const rest = options.filter(option => known.indexOf(option.sku) === -1)
    return { fromHistory, rest }
}

export const manualSkuOptionLabel = option => (option.administrative ? `${option.sku} (קוד עמלה)` : option.sku)

// The second confirmation: the code the operator chose, and nothing else.
export const manualSkuConfirmText = sku => `נבחר מק״ט ${sku}. לשייך אותו לעסקה?`

export const MANUAL_SKU_TEXT = {
    success: 'המק״ט שויך',
    changed: AUTO_MATCH_TEXT.changed,
    error: 'שיוך המק״ט נכשל',
    loadError: 'טעינת היסטוריית המק״טים נכשלה',
    none: 'לא נמצא',
    noHistory: 'לא נמצאה היסטוריית מק״ט ללקוח'
}
